import React, { useEffect, useMemo, useState } from 'react';
import { Building2, CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Loader2, MessageSquare, Video } from 'lucide-react';

// Emploi du temps de deux jours (aujourd'hui et le prochain jour ouvré par défaut ; flèches pour avancer ou reculer),
// en grille horaire compacte : couleur par projet, ligne de l'heure courante ; le lieu de travail (« Bureau ») est
// une simple étiquette du jour.

const HOUR_PX = 44;
const DAY_MINUTES = 24 * 60;
const NOT_MEETINGS = ['workingLocation', 'focusTime', 'outOfOffice'];

const TONES = [
  'border-indigo-500 bg-indigo-50 text-indigo-950 hover:bg-indigo-100',
  'border-emerald-500 bg-emerald-50 text-emerald-950 hover:bg-emerald-100',
  'border-sky-500 bg-sky-50 text-sky-950 hover:bg-sky-100',
  'border-violet-500 bg-violet-50 text-violet-950 hover:bg-violet-100',
  'border-teal-500 bg-teal-50 text-teal-950 hover:bg-teal-100',
  'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-950 hover:bg-fuchsia-100',
];

const eventTone = (event) => {
  if (event.project_id) return TONES[event.project_id % TONES.length];
  return event.external?.length ? 'border-amber-500 bg-amber-50 text-amber-950 hover:bg-amber-100'
    : 'border-slate-400 bg-slate-100 text-slate-800 hover:bg-slate-200';
};

const minutesOf = (iso, dayIso) => {
  const date = new Date(iso);
  const local = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  if (local > dayIso) return DAY_MINUTES;
  if (local < dayIso) return 0;
  return date.getHours() * 60 + date.getMinutes();
};

// Réunions qui se chevauchent : côte à côte.
const layoutDay = (events, dayIso) => {
  const timed = events.filter((e) => !e.all_day).map((event) => {
    const start = minutesOf(event.start, dayIso);
    const end = event.end ? Math.max(minutesOf(event.end, dayIso), start + 15) : start + 30;
    return { event, start, end };
  }).sort((a, b) => a.start - b.start || b.end - a.end);
  const placed = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const columns = [];
    cluster.forEach((item) => {
      let column = columns.findIndex((end) => end <= item.start);
      if (column < 0) {
        column = columns.length;
        columns.push(item.end);
      } else {
        columns[column] = item.end;
      }
      item.column = column;
    });
    cluster.forEach((item) => placed.push({ ...item, columns: columns.length }));
    cluster = [];
  };
  timed.forEach((item) => {
    if (cluster.length && item.start >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.end);
  });
  if (cluster.length) flush();
  return placed;
};

const dayTitle = (iso, today) => {
  const date = new Date(`${iso}T12:00:00`);
  const tomorrow = new Date(`${today}T12:00:00`);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const label = iso === today ? "Aujourd'hui" : iso === tomorrow.toISOString().slice(0, 10) ? 'Demain'
    : date.toLocaleDateString('fr-FR', { weekday: 'long' });
  return { label: label.charAt(0).toUpperCase() + label.slice(1), date: date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) };
};

// Téléphone : une liste par jour (heure, titre, projet), plus lisible qu'une grille horaire étroite.
const DayList = ({ view, today, now, openPoints, onOpenEvent }) => {
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return (
    <div className="space-y-4 px-4 pb-4 sm:hidden">
      {view.days.map((day) => {
        const title = dayTitle(day.date, today);
        const isToday = day.date === today;
        return (
          <div key={day.date}>
            <p className={`flex items-baseline gap-1.5 text-sm font-semibold ${isToday ? 'text-indigo-700' : 'text-slate-700'}`}>
              {title.label} <span className="font-normal text-slate-400">{title.date}</span>
              {day.places.length > 0 && (
                <span className="ml-auto flex items-center gap-1 truncate text-[11px] font-normal text-slate-500"><Building2 size={11} /> {day.places.join(', ')}</span>
              )}
            </p>
            {day.allDay.map((event) => (
              <button key={event.id} type="button" onClick={() => onOpenEvent(event, day)}
                className={`mt-1.5 block w-full truncate rounded-md border-l-2 px-2 py-1 text-left text-xs font-medium ${eventTone(event)}`}>
                {event.title}
              </button>
            ))}
            {day.items.length === 0 && day.allDay.length === 0 && <p className="mt-1 text-xs text-slate-400">Aucune réunion.</p>}
            <div className="mt-1.5 space-y-1.5">
              {day.items.map(({ event, end }) => {
                const past = isToday && end <= nowMinutes;
                const points = openPoints[event.id];
                return (
                  <button key={event.id} type="button" onClick={() => onOpenEvent(event, day)}
                    className={`flex w-full items-center gap-3 rounded-lg border-l-[3px] px-2.5 py-2 text-left ${eventTone(event)} ${past ? 'opacity-50' : ''}`}>
                    <span className="w-12 shrink-0 text-xs font-semibold tabular-nums">
                      {event.time}{event.end_time && <span className="block font-normal opacity-60">{event.end_time}</span>}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1 text-sm font-medium leading-snug">
                        {event.link && <Video size={12} className="shrink-0 opacity-60" />}
                        <span className="truncate">{event.title}</span>
                      </span>
                      {event.project_name && <span className="block truncate text-xs opacity-70">{event.project_name}</span>}
                    </span>
                    {points > 0 && <span className="flex shrink-0 items-center gap-0.5 text-xs text-indigo-700"><MessageSquare size={11} />{points}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DayAgenda = ({ schedule, today, openPoints, onOpenEvent, navigate, onMove, moving }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const view = useMemo(() => {
    if (!schedule?.days?.length) return null;
    let first = 9 * 60;
    let last = 18 * 60;
    const days = schedule.days.map((day) => {
      const meetings = day.events.filter((e) => !NOT_MEETINGS.includes(e.event_type));
      const items = layoutDay(meetings, day.date);
      items.forEach((item) => {
        first = Math.min(first, item.start);
        last = Math.max(last, item.end);
      });
      return {
        ...day, items,
        allDay: meetings.filter((e) => e.all_day),
        places: [...new Set(day.events.filter((e) => e.event_type === 'workingLocation').map((e) => e.title))],
      };
    });
    return { days, startHour: Math.max(0, Math.floor(first / 60)), endHour: Math.min(24, Math.ceil(last / 60)),
      hasAllDay: days.some((d) => d.allDay.length) };
  }, [schedule]);

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const height = view ? (view.endHour - view.startHour) * HOUR_PX : 0;

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-4 sm:px-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <CalendarDays size={16} className="text-slate-400" /> Emploi du temps
          {moving && <Loader2 size={13} className="animate-spin text-slate-400" />}
        </h2>
        {schedule.connected && (
          <div className="flex items-center gap-1">
            {onMove && (
              <>
                <button type="button" onClick={() => onMove(-1)} disabled={moving} title="Jour précédent"
                  className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"><ChevronLeft size={16} /></button>
                {(schedule.start || today) !== today && (
                  <button type="button" onClick={() => onMove(0)} disabled={moving}
                    className="rounded-md px-2 py-0.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Aujourd'hui</button>
                )}
                <button type="button" onClick={() => onMove(1)} disabled={moving} title="Jour suivant"
                  className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"><ChevronRight size={16} /></button>
              </>
            )}
            <a href="https://calendar.google.com/calendar/r/week" target="_blank" rel="noreferrer" title="Agenda Google"
              className="ml-1 flex items-center gap-1 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <ExternalLink size={13} />
            </a>
          </div>
        )}
      </div>
      {!schedule.connected ? (
        <div className="px-5 pb-6 pt-2 text-sm text-slate-500">
          <p>Relie ton agenda Google pour voir tes réunions ici et les préparer avec Nexus.</p>
          <button type="button" onClick={() => navigate('/ai-preferences')} className="mt-3 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
            Relier mon agenda
          </button>
        </div>
      ) : schedule.error ? (
        <p className="px-5 pb-5 text-sm text-rose-600">{schedule.error}</p>
      ) : view && (
        <>
        <div className={`transition-opacity ${moving ? 'opacity-50' : ''}`}>
          <DayList view={view} today={today} now={now} openPoints={openPoints} onOpenEvent={onOpenEvent} />
        </div>
        <div className={`hidden pb-3 pr-3 transition-opacity sm:grid ${moving ? 'opacity-50' : ''}`} style={{ gridTemplateColumns: `40px repeat(${view.days.length}, minmax(0, 1fr))` }}>
          <div />
          {view.days.map((day) => {
            const title = dayTitle(day.date, today);
            return (
              <div key={day.date} className="px-1.5 pb-2">
                <p className={`text-sm font-semibold ${day.date === today ? 'text-indigo-700' : 'text-slate-700'}`}>
                  {title.label} <span className="font-normal text-slate-400">{title.date}</span>
                </p>
                {day.places.length > 0 && (
                  <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-500"><Building2 size={11} /> {day.places.join(', ')}</p>
                )}
              </div>
            );
          })}

          {view.hasAllDay && (
            <>
              <div />
              {view.days.map((day) => (
                <div key={day.date} className="space-y-0.5 px-1 pb-1">
                  {day.allDay.map((event) => (
                    <button key={event.id} type="button" onClick={() => onOpenEvent(event, day)} title={event.title}
                      className={`block w-full truncate rounded border-l-2 px-1.5 py-0.5 text-left text-[11px] font-medium ${eventTone(event)}`}>
                      {event.title}
                    </button>
                  ))}
                </div>
              ))}
            </>
          )}

          <div className="relative" style={{ height }}>
            {Array.from({ length: view.endHour - view.startHour + 1 }, (_, i) => (
              <span key={i} className="absolute right-1.5 -translate-y-1/2 text-[10px] tabular-nums text-slate-400" style={{ top: i * HOUR_PX }}>
                {view.startHour + i} h
              </span>
            ))}
          </div>
          {view.days.map((day) => {
            const isToday = day.date === today;
            return (
              <div key={day.date} className={`relative mx-0.5 rounded-lg ${isToday ? 'bg-indigo-50/30' : 'bg-slate-50/50'}`} style={{ height }}>
                {Array.from({ length: view.endHour - view.startHour + 1 }, (_, i) => (
                  <div key={i} className="absolute inset-x-0 border-t border-slate-100" style={{ top: i * HOUR_PX }} />
                ))}
                {day.items.map(({ event, start, end, column, columns }) => {
                  const top = ((start - view.startHour * 60) / 60) * HOUR_PX;
                  const blockHeight = Math.max(((end - start) / 60) * HOUR_PX - 2, 18);
                  const past = isToday && end <= nowMinutes;
                  const points = openPoints[event.id];
                  return (
                    <button key={event.id} type="button" onClick={() => onOpenEvent(event, day)}
                      className={`absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-0.5 text-left shadow-sm transition ${eventTone(event)} ${past ? 'opacity-50' : ''}`}
                      style={{ top: top + 1, height: blockHeight, left: `calc(${(column / columns) * 100}% + 2px)`, width: `calc(${100 / columns}% - 4px)` }}
                      title={`${event.time || ''}${event.end_time ? `–${event.end_time}` : ''} ${event.title}`}>
                      <p className="flex items-center gap-1 text-[11px] font-semibold leading-tight">
                        {event.link && <Video size={10} className="shrink-0 opacity-60" />}
                        <span className="truncate">{event.title}</span>
                        {points > 0 && <span className="ml-auto flex shrink-0 items-center gap-0.5 text-[10px] text-indigo-700"><MessageSquare size={9} />{points}</span>}
                      </p>
                      {blockHeight > 30 && (
                        <p className="truncate text-[10px] leading-tight opacity-75">
                          {event.time}{event.end_time ? `–${event.end_time}` : ''}{event.project_name ? ` · ${event.project_name}` : ''}
                        </p>
                      )}
                    </button>
                  );
                })}
                {isToday && nowMinutes >= view.startHour * 60 && nowMinutes <= view.endHour * 60 && (
                  <div className="pointer-events-none absolute inset-x-0 z-10 flex items-center" style={{ top: ((nowMinutes - view.startHour * 60) / 60) * HOUR_PX }}>
                    <span className="-ml-1 h-2 w-2 rounded-full bg-rose-500" />
                    <span className="h-px flex-1 bg-rose-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}
    </section>
  );
};

export default DayAgenda;
